import { Popover, PopoverContent, PopoverTrigger } from '@chakra-ui/react';
import { CloseIcon, CustomDateIcon } from '@sketchnote/icons';
import { Calendar, CalendarTabs } from '@sketchnote/shared-components';
import cn from 'classnames';
import { format, formatISO, parseISO } from 'date-fns';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { OnChangeDateRangeCallback } from 'react-calendar';
import { v4 as uuidv4 } from 'uuid';

import { editSprint } from '../../api/useAgileService';
import { Sprint, useAgileStore } from '../../store';

const randomColor = [
  '#fbb6ce',
  '#d6bcfa',
  '#9BADEE',
  '#81e6d9',
  '#86E495',
  '#FFE98F',
  '#fbd38d',
  '#FBB1A7',
];

interface SprintProps {
  onClose: any;
  id: string;
  data: Sprint;
}

interface DateRangePickerProps {
  value: [Date] | [Date, Date] | null;
  onChange: OnChangeDateRangeCallback;
  inputClassName?: string;
  placeholderText?: string;
  dateFormat?: string;
  initialTab?: CalendarTabs;
}

export const SprintModal = ({ onClose, id, data: sprint }: SprintProps) => {
  const [isSaving, setSaving] = useState(false);
  const [startDate, setStartDate] = useState<any>('');
  const [endDate, setEndDate] = useState<any>('');
  const [name, setName] = useState(sprint.title);
  const [description, setDescription] = useState(sprint.description);
  const inputRef = useRef<HTMLInputElement>(null);
  const { addSprint, startSprint } = useAgileStore();
  const router = useRouter();
  const fileRef = router?.query?.slug as string;

  // useEffect(() => {}); fetch sprint by Id

  const handleStartDate = (values) => {
    setStartDate(values);
  };
  const handleEndDate = (values) => {
    setEndDate(values);
  };

  const handleSprint = async () => {
    if (startDate && endDate) {
      startSprint(id, startDate, endDate);
      onClose();
    }
    return;
  };
  return (
    <div className="p-24">
      <div className="flex justify-between mb-8">
        <span className="text-primary-700 font-semibold text-xl">Start sprint</span>
        <button aria-label="close button" onClick={onClose}>
          <CloseIcon className="text-gray-600 text-xs" />
        </button>
      </div>

      <div className="w-full mb-20">
        <label className="mb-8 block text-sm text-gray-500 font-medium">Name</label>
        <input
          type="text"
          className="border w-full text-base placeholder-text-gray-500 focus:shadow-outline border-gray-200 px-12 py-10 bg-gray-100 rounded-lg placeholder-text-base"
          value={name}
          disabled
        />
      </div>
      <div className="w-full mb-20">
        <label className="mb-8 block  text-sm text-gray-500 font-medium">Description</label>
        <textarea
          value={description}
          disabled
          className="border w-full text-base placeholder-text-gray-500 focus:shadow-outline border-gray-200 px-12 py-10 bg-gray-100 rounded-lg placeholder-text-base"
        />
      </div>
      <div className="w-full mb-20 flex items-center gap-10">
        <div className="w-1/2 h-full">
          <label className="mb-8 block  text-sm text-gray-500 font-medium">Start date</label>
          <DateRangePicker
            value={startDate.length > 0 ? (startDate as [Date] | [Date, Date]) : null}
            onChange={handleStartDate}
            inputClassName="bg-gray-100 p-12 w-full rounded-sm h-48 border border-gray-200 text-left"
            initialTab={startDate.length === 1 ? 'startEndDate' : 'date'}
          />
        </div>
        <div className="w-1/2 h-full">
          <label className="mb-8 block  text-sm text-gray-500 font-medium">End date</label>
          <DateRangePicker
            value={endDate.length > 0 ? (endDate as [Date] | [Date, Date]) : null}
            onChange={handleEndDate}
            inputClassName="bg-gray-100 p-12 w-full rounded-sm h-48 border border-gray-200 text-left"
            initialTab={endDate.length === 1 ? 'startEndDate' : 'date'}
          />
        </div>
      </div>

      <div className="flex justify-start  w-full">
        <button
          onClick={onClose}
          className="border-blue-500 text-blue-500 border rounded-sm px-24 py-8 mr-8 w-1/2"
        >
          Back
        </button>
        <button
          className={` text-white border rounded-sm px-24 py-8  w-1/2 ${
            isSaving ? 'bg-blue-200' : 'bg-blue-500'
          }`}
          onClick={handleSprint}
          disabled={isSaving}
        >
          Start sprint
        </button>
      </div>
    </div>
  );
};

const DateRangePicker = ({
  value,
  onChange,
  inputClassName,
  placeholderText = 'Select date',
  dateFormat = 'dd/MM/yyyy',
  initialTab = 'date',
}: DateRangePickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const open = () => setIsOpen(!isOpen);
  const close = () => setIsOpen(false);

  const handleDatesForInput = (values) => {
    if (!values) return placeholderText;
    else if (values.length === 1) return `${format(values[0], dateFormat)}`;
    else if (values.length > 1)
      return `${format(values[0], dateFormat)} - ${format(values[1], dateFormat)}`;
    return '';
  };

  const handleDoneClick = () => {
    setIsOpen(false);
  };

  return (
    <Popover isOpen={isOpen} onClose={close} onOpen={open}>
      <PopoverTrigger>
        <div
          onClick={open}
          className="border w-full text-base placeholder-text-gray-500 focus:shadow-outline border-gray-200 px-12 py-10 bg-gray-100 rounded-lg placeholder-text-base "
        >
          <div className="flex items-center gap-4">
            <CustomDateIcon size={14} wrapperClass={'text-gray-500'} />
            <button
              className={`${
                handleDatesForInput(value) === placeholderText ? 'text-gray-500' : 'text-gray-999'
              }`}
            >
              {handleDatesForInput(value)}
            </button>
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent zIndex={4}>
        <Calendar
          value={value}
          onChange={onChange}
          onDone={handleDoneClick}
          initialTab={initialTab}
          isScrum={true}
        />
      </PopoverContent>
    </Popover>
  );
};
